/**
 * useUISounds — общие звуки UI Luna.
 *
 * Версия 3 (18 Apr 2026): минимальный HTMLAudio + пул, БЕЗ unlock-логики.
 *
 * Почему без unlock: любой синхронный Audio.play() внутри user-gesture
 * (клик по кнопке) в iOS Telegram WebView разрешается без отдельного
 * "прогрева". Специальная unlock-логика (silent play пула) создавала
 * слышимые всплески и/или ломала звук вовсе. Этот минимальный вариант
 * работает надёжно: каждый play() ротирует пул из 3 копий, ставит
 * currentTime=0, volume и play(). MP3 физически зафадены (5% in/out),
 * щелчков нет.
 */
import { useCallback, useRef } from 'react';
import { getUserSettings } from './useUserSettings';

type SoundName =
  | 'uiTap'
  | 'uiNavTab'
  | 'uiCardPress'
  | 'uiWalletTab'
  | 'bellOpen'
  | 'bellClose'
  | 'bellClear'
  | 'badgePop'
  | 'modalOpen'
  | 'modalClose'
  | 'toastInfo'
  | 'toastError'
  | 'copySuccess'
  | 'lineExpand'
  | 'claimStart'
  | 'claimSuccess'
  | 'plasmaCoreTap'
  | 'plasmaCoreRecharge'
  | 'plasmaCoreActivate'
  | 'plasmaCoreDone'
  | 'slotCardTap'
  | 'nodeTap'
  | 'slotDepositConfirm'
  | 'slotDepositConfirmA'
  | 'slotDepositConfirmB'
  | 'slotDepositConfirmC'
  | 'slotConfirmed'
  | 'p2pSend'
  | 'p2pError'
  | 'quickstartTick'
  | 'accessRequest'
  | 'paymentTap';

const SOUND_FILES: Record<SoundName, string> = {
  uiTap: '/sounds/ui_tap.mp3',
  uiNavTab: '/sounds/ui_nav_tab.mp3',
  uiCardPress: '/sounds/ui_card_press.mp3',
  uiWalletTab: '/sounds/ui_wallet_tab.mp3',
  bellOpen: '/sounds/bell_open.mp3',
  bellClose: '/sounds/bell_close.mp3',
  bellClear: '/sounds/bell_clear.mp3',
  badgePop: '/sounds/badge_pop.mp3',
  modalOpen: '/sounds/modal_open.mp3',
  modalClose: '/sounds/modal_close.mp3',
  toastInfo: '/sounds/toast_info.mp3',
  toastError: '/sounds/toast_error.mp3',
  copySuccess: '/sounds/copy_success.mp3',
  lineExpand: '/sounds/line_expand.mp3',
  claimStart: '/sounds/claim_start.mp3',
  claimSuccess: '/sounds/claim_success.mp3',
  plasmaCoreTap: '/sounds/plasma_core_tap.mp3',
  plasmaCoreRecharge: '/sounds/plasma_core_recharge.mp3',
  plasmaCoreActivate: '/sounds/plasma_core_activate.mp3',
  plasmaCoreDone: '/sounds/plasma_core_done.mp3',
  slotCardTap: '/sounds/slot_card_tap.mp3',
  nodeTap: '/sounds/node_tap.mp3',
  // slotDepositConfirm теперь случайно выбирается между 3 AirPods Pro × Cyberpunk
  // 12-секундными вариантами (a: v3#5 warm sparkle, b: v3#1 cinematic flow,
  // c: v3#3 slow dreamy buildup). Звук играется через рандомайзер в useUISounds
  // ниже — сам ключ slotDepositConfirm напрямую не используется.
  slotDepositConfirm: '/sounds/slot_deposit_confirm_a.mp3',
  slotDepositConfirmA: '/sounds/slot_deposit_confirm_a.mp3',
  slotDepositConfirmB: '/sounds/slot_deposit_confirm_b.mp3',
  slotDepositConfirmC: '/sounds/slot_deposit_confirm_c.mp3',
  slotConfirmed: '/sounds/slot_confirmed.mp3',
  p2pSend: '/sounds/p2p_send.mp3',
  p2pError: '/sounds/p2p_error.mp3',
  quickstartTick: '/sounds/quickstart_tick.mp3',
  accessRequest: '/sounds/access_request.mp3',
  paymentTap: '/sounds/payment_tap.mp3?v=3',
};

const VOLUME_UI = 0.22;
const VOLUME_FEEDBACK = 0.25;
const VOLUME_CINEMATIC = 0.28;

function getPeak(name: SoundName): number {
  if (
    name === 'claimSuccess' ||
    name === 'plasmaCoreActivate' ||
    name === 'plasmaCoreRecharge' ||
    name === 'slotDepositConfirm' ||
    name === 'accessRequest'
  ) {
    return VOLUME_CINEMATIC;
  }
  if (
    name === 'toastError' ||
    name === 'p2pError' ||
    name === 'claimStart' ||
    name === 'p2pSend' ||
    name === 'copySuccess' ||
    name === 'modalOpen' ||
    name === 'modalClose' ||
    name === 'bellOpen'
  ) {
    return VOLUME_FEEDBACK;
  }
  return VOLUME_UI;
}

// ────────────────────────────────────────────────────────────────────
// Sound architecture v6 (23 Apr 2026) — портирована из Syntharis/
// Vanguardis SoundManager, работает стабильно в Telegram WebView.
//
// Ключевые отличия от прежней реализации Luna:
//   1. НЕ пулим Audio элементы — `new Audio(src)` на КАЖДЫЙ play().
//      На iOS лимит ~16 одновременных Audio — pool×5×30 = 150 уходил
//      в очередь, play() зависал.
//   2. Prefetch через `fetch(src).catch(...)` прогревает HTTP cache
//      БЕЗ создания Audio элементов — не упирается в лимит.
//   3. Unlock: создание AudioContext в user-gesture + silent play
//      одного Audio. После разблокировки все последующие play() идут.
//   4. До unlock — все play() игнорируются тихо (как в Vanguardis).
// ────────────────────────────────────────────────────────────────────

let unlocked = false;
let ctx: AudioContext | null = null;
const prefetched = new Set<string>();

function prefetchOne(url: string): void {
  if (prefetched.has(url)) return;
  prefetched.add(url);
  // Not a data URL — прогреваем HTTP cache. iOS ограничивает одновре-
  // менные Audio элементы; fetch их не создаёт и не упирается в лимит.
  if (url.startsWith('data:')) return;
  fetch(url).catch(() => {});
}

// Prefetch всех звуков после старта (не блокирует первый paint)
if (typeof window !== 'undefined') {
  window.setTimeout(() => {
    const names = Object.keys(SOUND_FILES) as SoundName[];
    names.forEach((n, i) => {
      window.setTimeout(() => prefetchOne(SOUND_FILES[n]), i * 50);
    });
  }, 300);
}

function unlock(): void {
  if (unlocked) return;
  if (typeof window === 'undefined') return;
  try {
    // Создание AudioContext ВНУТРИ user-gesture — критично для iOS.
    // Если создать вне gesture, контекст останется suspended навсегда.
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AC && !ctx) {
      ctx = new AC();
    }
    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().catch(() => {});
    }
    // Silent Audio play — разблокирует HTML5 Audio stack на iOS.
    // Пусть один звук играет — бюджет user-gesture не съедает
    // т.к. это muted/volume=0.
    try {
      const s = new Audio(SOUND_FILES.uiTap);
      s.volume = 0;
      void s.play().then(() => { s.pause(); }).catch(() => {});
    } catch { /* silent */ }
    unlocked = true;
  } catch {
    // silent
  }
}

// Слушатель первого взаимодействия — unlock один раз.
if (typeof window !== 'undefined') {
  const onFirst = () => {
    if (unlocked) return;
    unlock();
  };
  document.addEventListener('click', onFirst, { passive: true });
  document.addEventListener('touchstart', onFirst, { passive: true });
  document.addEventListener('pointerdown', onFirst, { passive: true });
}

function play(name: SoundName): () => void {
  if (!getUserSettings().soundsEnabled) return () => {};
  if (!unlocked) {
    // Пробуем unlock если не инициализирован (работает только в
    // user-gesture, т.е. если play вызван из click handler).
    unlock();
    if (!unlocked) return () => {};
  }
  const src = SOUND_FILES[name];
  if (!src) return () => {};

  // Глобальный cache-bust: ко всем mp3 добавляется ?v=ios100, чтобы
  // браузер пересосал свежие файлы с +100ms silence-pad в хвосте.
  // Если URL уже содержит ?v=... (как paymentTap) — оставляем его.
  const finalSrc = src.includes('?v=') ? src : `${src}?v=ios100`;

  let audio: HTMLAudioElement | null = null;
  try {
    audio = new Audio(finalSrc);
    audio.volume = getPeak(name);
    void audio.play().catch(() => { /* silent */ });
  } catch {
    // silent
  }
  return () => {
    try { audio?.pause(); } catch { /* silent */ }
  };
}

// Совместимость с callsites: старое имя, внутри теперь triggers
// Vanguardis-style unlock (создание AudioContext в user-gesture).
export function warmupAllPools(): void {
  unlock();
}

export function useUISounds() {
  const lastTapRef = useRef(0);
  // Ничего не создаём в useEffect — пулы lazy-создаются при первом play()

  const tap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 30) return;
    lastTapRef.current = now;
    play('uiTap');
  }, []);

  const navTab = useCallback(() => play('uiNavTab'), []);
  const cardPress = useCallback(() => play('uiCardPress'), []);
  const walletTab = useCallback(() => play('uiWalletTab'), []);
  const bellOpen = useCallback(() => play('bellOpen'), []);
  const bellClose = useCallback(() => play('bellClose'), []);
  const bellClear = useCallback(() => play('bellClear'), []);
  const badgePop = useCallback(() => play('badgePop'), []);
  const modalOpen = useCallback(() => play('modalOpen'), []);
  const modalClose = useCallback(() => play('modalClose'), []);
  const toastInfo = useCallback(() => play('toastInfo'), []);
  const toastError = useCallback(() => play('toastError'), []);
  const copySuccess = useCallback(() => play('copySuccess'), []);
  const lineExpand = useCallback(() => play('lineExpand'), []);
  const claimStart = useCallback(() => play('claimStart'), []);
  const claimSuccess = useCallback(() => play('claimSuccess'), []);
  const plasmaCoreTap = useCallback(() => play('plasmaCoreTap'), []);
  const plasmaCoreRecharge = useCallback(() => play('plasmaCoreRecharge'), []);
  const plasmaCoreActivate = useCallback(() => play('plasmaCoreActivate'), []);
  const plasmaCoreDone = useCallback(() => play('plasmaCoreDone'), []);
  const slotCardTap = useCallback(() => play('slotCardTap'), []);
  const nodeTap = useCallback(() => play('nodeTap'), []);
  // Слот-purchase звук рандомно выбирается из 3 вариантов, чтобы юзер
  // не слышал одну и ту же запись каждый раз. Все три — AirPods Pro ×
  // Cyberpunk 12-секундные семплы, разные по атмосфере.
  const slotDepositConfirm = useCallback(() => {
    const variant = Math.floor(Math.random() * 3);
    if (variant === 0) return play('slotDepositConfirmA');
    if (variant === 1) return play('slotDepositConfirmB');
    return play('slotDepositConfirmC');
  }, []);
  const slotConfirmed = useCallback(() => play('slotConfirmed'), []);
  const p2pSend = useCallback(() => play('p2pSend'), []);
  const p2pError = useCallback(() => play('p2pError'), []);
  const quickstartTick = useCallback(() => play('quickstartTick'), []);
  const accessRequest = useCallback(() => play('accessRequest'), []);
  const paymentTap = useCallback(() => play('paymentTap'), []);

  return {
    tap,
    navTab,
    cardPress,
    walletTab,
    bellOpen,
    bellClose,
    bellClear,
    badgePop,
    modalOpen,
    modalClose,
    toastInfo,
    toastError,
    copySuccess,
    lineExpand,
    claimStart,
    claimSuccess,
    plasmaCoreTap,
    plasmaCoreRecharge,
    plasmaCoreActivate,
    plasmaCoreDone,
    slotCardTap,
    nodeTap,
    slotDepositConfirm,
    slotConfirmed,
    p2pSend,
    p2pError,
    quickstartTick,
    accessRequest,
    paymentTap,
  };
}
